import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { useAppDispatch } from '../reduxhooks';
import React, { useEffect, useRef } from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Box } from '@material-ui/core';
import { useInView } from 'react-intersection-observer';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import { CarouselItemData } from './PageComponentElementBlocks';
import { sendWebsiteEventMessage } from "../telegrambotevents";

interface stylesProps {
  width: string,
  margin: string,
}

const useStyles=({width,margin}: stylesProps) => makeStyles((theme: Theme) =>
createStyles({
  carouselContainerBox: {
    position: "relative",
    // The current slide's natural aspect ratio is fed in as an inline CSS
    // custom property on this Box; the vendor slider-wrapper (overflow: hidden)
    // then sizes itself from the container width, so the block is always
    // exactly as tall as the media on screen. Falls back to `auto`
    // (= content sized) before any ratio is known.
    // `&&` doubles specificity so we win over vendor `.carousel .slider-wrapper`
    // regardless of stylesheet order.
    // Deliberately NOT transitioned: Chrome freezes aspect-ratio at its first
    // resolved value when it is both transitioned and driven by a custom
    // property, so the block would keep the first slide's height forever.
    '&& .slider-wrapper': {
      aspectRatio: 'var(--tb-carousel-ratio, auto)',
    },
  },
  svgImage: {
    width: width,
    margin: margin,
  },
  circularProgressContainer: {
    width: width,
    aspectRatio: '2',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: "300px 0px 300px 0px",
  },
  indicatorRow: {
    position: 'absolute',
    bottom: '10px',
    left: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    zIndex: 1,
    // The row spans the full width, so it must not swallow swipe gestures.
    // Only the dots themselves take pointer events.
    pointerEvents: 'none',
  },
  indicatorItemSelected: {
    display: 'inline-block',
    background: 'rgba(35, 35, 35, 0.85)',
    width: 7,
    height: 7,
    margin: '0px 3px',
    borderRadius: '50%',
    pointerEvents: 'auto',
  },
  indicatorItem: {
    display: 'inline-block',
    background: 'rgba(35, 35, 35, 0.35)',
    width: 7,
    height: 7,
    margin: '0px 3px',
    borderRadius: '50%',
    cursor: 'pointer',
    pointerEvents: 'auto',
  },
  cartCarouselImage: {
    width: '100%',
    // Block, so the inline baseline descender doesn't add a few stray pixels
    // under the media on the single-item path.
    display: 'block',
  },
  cartCarouselVideoContainer: {
    position: "relative",
  },
  cartCarouselVideo: {
    width: '100%',
    // paddingBottom: '36px',
  },
  cartCarouselVideoSoundButton: {
    position: "absolute",
    bottom: "52px",
    right: "16px",
    color: "#ffffff",
    backgroundColor: "#232323",
    padding: "6px 6px 4px 6px",
    borderRadius: "18px",
  },
}),
);

export interface VideoComponentProps {
  source: string
  videoHasSound?: boolean
  videoPoster?: string
  videoSoundOn?: boolean
  widthSet?: string
  /** Optional: reports the video's intrinsic pixel size once metadata loads. */
  onNaturalSize?: (naturalWidth: number, naturalHeight: number) => void
  /** Optional: overrides the sound button's `bottom` offset for this instance. */
  soundButtonBottom?: string
}

export function VideoComponent({source, videoHasSound, videoPoster, videoSoundOn, widthSet, onNaturalSize, soundButtonBottom}: VideoComponentProps) {
  const width = widthSet ?? '100%';
  const margin = '0px 0px 0px 0px';
  const classes = useStyles({width, margin})();

  const [isMuted, setIsMuted] = React.useState<boolean>(true);
  const videoElement = useRef(null);

  // Latched in a ref so the effect below doesn't depend on an inline arrow
  // that changes identity on every parent render.
  const onNaturalSizeRef = useRef(onNaturalSize);
  onNaturalSizeRef.current = onNaturalSize;

  // Covers the case where metadata is already available on mount (cached video,
  // or React reusing the <video> node across a src swap) so onLoadedMetadata
  // never fires.
  useEffect(() => {
    const video = videoElement.current as HTMLVideoElement | null;
    if (video && video.readyState >= 1 && video.videoWidth && video.videoHeight) {
      onNaturalSizeRef.current?.(video.videoWidth, video.videoHeight);
    }
  }, [source]);

  // Intersection Observer API
  const { ref, inView, entry } = useInView({
    threshold: 0.4,
    triggerOnce: false,
    rootMargin: '10px',
  });

  useEffect(() => {
    if (inView) {
      var isPlaying = (videoElement as any)?.current?.currentTime > 0 && !(videoElement as any)?.current?.paused && !(videoElement as any)?.current?.ended 
      && (videoElement as any)?.current?.readyState > (videoElement as any)?.current?.HAVE_CURRENT_DATA;
      if (!isPlaying) {
        (videoElement as any)?.current?.play()?.catch(function(){});;
      }
    } else {
      var isPlaying = (videoElement as any)?.current?.currentTime > 0 && !(videoElement as any)?.current?.paused && !(videoElement as any)?.current?.ended 
      && (videoElement as any)?.current?.readyState > (videoElement as any)?.current?.HAVE_CURRENT_DATA;
      if (isPlaying) {
        (videoElement as any)?.current?.pause()?.catch(function(){});;
      }
    }
  }, [inView]);
  
  return (
  <div ref={ref} className={classes.cartCarouselVideoContainer}>
    <video
      ref={videoElement}
      src={source}
      autoPlay
      playsInline
      loop
      muted={videoHasSound === false || (videoSoundOn === undefined && isMuted) || videoSoundOn === false}
      poster={
        videoPoster ? 
        videoPoster : 
        undefined
      }
      className={classes.cartCarouselVideo}
      preload='auto'
      onLoadedMetadata={(e) => {
        const video = e.currentTarget;
        if (video.videoWidth && video.videoHeight) {
          onNaturalSizeRef.current?.(video.videoWidth, video.videoHeight);
        }
      }}
      >
    </video>
    {
      videoHasSound === undefined || videoHasSound === false || videoSoundOn === true || videoSoundOn === false ? 
      undefined :
      isMuted ?
      <div className={classes.cartCarouselVideoSoundButton}
      style={soundButtonBottom ? { bottom: soundButtonBottom } : undefined}
      onClick={() => {
        setIsMuted(false);
      }}>
        <VolumeOffIcon />
      </div>:
      <div className={classes.cartCarouselVideoSoundButton}
      style={soundButtonBottom ? { bottom: soundButtonBottom } : undefined}
      onClick={() => {
        setIsMuted(true);
      }}>
        <VolumeUpIcon />
      </div>
    }
  </div>);
}

interface PageProductItemProps {
  carouselItemDataArray: CarouselItemData[]
  linkTo?: string,
  margin?: string,
}

export default function PageElementCarousel({carouselItemDataArray, linkTo, margin: marginUsed} : PageProductItemProps) {
  const width = '100%';
  const margin = marginUsed ? marginUsed : '0px 0px 0px 0px';
  const classes = useStyles({width, margin})();
  const [imageOverlayIndex, setImageOverlayIndex] = React.useState<number>(0);

  // Natural aspect ratios of the media, keyed by contentUrl rather than index:
  // index keys would go stale the moment the layout editor reorders or deletes
  // an item, and url keys dedupe the duplicate load events fired by the
  // infiniteLoop clones.
  const [ratioByUrl, setRatioByUrl] = React.useState<Record<string, number>>({});

  const recordRatio = React.useCallback((url: string, naturalWidth: number, naturalHeight: number) => {
    if (!url || !naturalWidth || !naturalHeight) return;
    const ratio = naturalWidth / naturalHeight;
    // Bailing out when unchanged is what stops the clone load events from
    // looping renders.
    setRatioByUrl((prev) => (prev[url] === ratio ? prev : { ...prev, [url]: ratio }));
  }, []);

  const itemCount = carouselItemDataArray?.length ?? 0;
  // The editor can shrink the array while imageOverlayIndex still points past
  // the end.
  const safeIndex = itemCount > 0
    ? Math.min(Math.max(imageOverlayIndex, 0), itemCount - 1)
    : 0;
  const currentRatio = ratioByUrl[carouselItemDataArray?.[safeIndex]?.contentUrl ?? ''];

  // Hold the last known ratio while the next slide's media is still loading —
  // far better than briefly collapsing to `auto`, which sizes to the tallest
  // slide. Matters most for video, whose ratio only arrives on loadedmetadata.
  const lastRatioRef = useRef<number | undefined>(undefined);
  if (currentRatio !== undefined) {
    lastRatioRef.current = currentRatio;
  }
  const appliedRatio = currentRatio ?? lastRatioRef.current;

  const ratioStyle = {
    ['--tb-carousel-ratio' as any]: appliedRatio !== undefined ? String(appliedRatio) : 'auto',
  } as React.CSSProperties;

  if (carouselItemDataArray?.length === 1) {
    return (
      // A freshly added block has an empty contentUrl and would otherwise
      // collapse to 0px, making it unselectable in the editor preview.
      <Box
        className={classes.carouselContainerBox}
        style={carouselItemDataArray[0].contentUrl ? undefined : { minHeight: '36px' }}
      >
        {
          carouselItemDataArray[0].contentType === "image" ? 
          <div>
            <img src={carouselItemDataArray[0].contentUrl} className={classes.cartCarouselImage}/>
          </div> :
          carouselItemDataArray[0].contentType === "video-with-sound" ? 
          <VideoComponent 
            source={carouselItemDataArray[0].contentUrl} 
            videoHasSound={true} 
            videoPoster={carouselItemDataArray[0].videoPoster ? carouselItemDataArray[0].videoPoster : undefined}/> :
          carouselItemDataArray[0].contentType === "video-no-sound" ?
          <VideoComponent 
            source={carouselItemDataArray[0].contentUrl} 
            videoHasSound={false} 
            videoPoster={carouselItemDataArray[0].videoPoster ? carouselItemDataArray[0].videoPoster : undefined}/> :
          null
        }
      </Box>
    )
  }

  return (
    <Box className={classes.carouselContainerBox} style={ratioStyle}>
      <Carousel
        selectedItem={safeIndex}
        preventMovementUntilSwipeScrollTolerance={true}
        swipeScrollTolerance={12}
        showThumbs={false}
        showStatus={false}
        showArrows={false}
        showIndicators={false} // WE USE SEPARATE INDICATORS, OVERLAID ON THE MEDIA
        autoPlay={true}
        interval={2450}
        infiniteLoop={true}
        // Height comes from the aspect-ratio custom property above instead:
        // the library only measures <img>, so video slides never got a height.
        dynamicHeight={false}
        onChange={ (index, item) => {
          if (imageOverlayIndex !== index) {
            setImageOverlayIndex(index);
            // sendWebsiteEventMessage("User slide hero section " + carouselItemDataArray[0].contentUrl + "df" + " ke slide " + index);
          }
        }}
        >
        {
          carouselItemDataArray.map((carouselItemData, i) => {
            const contentType = carouselItemData.contentType;
            const contentUrl = carouselItemData.contentUrl;
            // The url in the key forces a remount when the editor swaps a
            // media, guaranteeing a fresh load event; the index suffix keeps
            // duplicate urls distinct.
            const slideKey = contentUrl + '-' + i;

            if (contentType === "video-with-sound" || contentType === "video-no-sound") {
              return (
                <div key={slideKey}>
                  <VideoComponent
                    source={contentUrl}
                    videoHasSound={contentType === "video-with-sound"}
                    videoPoster={carouselItemData.videoPoster ? carouselItemData.videoPoster : undefined}
                    soundButtonBottom={'16px'}
                    onNaturalSize={(naturalWidth, naturalHeight) => recordRatio(contentUrl, naturalWidth, naturalHeight)}/>
                </div>
              );
            }

            // "image" and anything unrecognised both render as an image, as before.
            return (
              <div key={slideKey}>
                <img
                  src={contentUrl}
                  className={classes.cartCarouselImage}
                  onLoad={(e) => recordRatio(contentUrl, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}/>
              </div>
            );
          })
        }
      </Carousel>
      <Box className={classes.indicatorRow}>
        {
          carouselItemDataArray.map((carouselItemData, index) => {
            return (
              <div
                key={'carousel-dot-' + index}
                className={index === safeIndex ? classes.indicatorItemSelected : classes.indicatorItem}
                role="button"
                tabIndex={0}
                aria-current={index === safeIndex}
                aria-label={`Slide ${index + 1} of ${itemCount}`}
                title={`Slide ${index + 1}`}
                onClick={() => setImageOverlayIndex(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setImageOverlayIndex(index);
                }}
              />
            );
          })
        }
      </Box>
    </Box>
  );
}